import { convertToUTCDate, DateFormat, USER_FORMAT } from '@agilox/common';
import { CalendarWeekOutput, Datepickers, DatepickerType } from '@agilox/ui-common';
import {
	Component,
	computed,
	effect,
	inject,
	Inject,
	input,
	model,
	output,
	signal,
	Signal,
	untracked,
	viewChild,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { DatepickerInputComponent } from './datepicker-input/datepicker-input.component';
import { USER_DATE_FORMATS } from './user-date-format-map.const';

@Component({
	selector: 'ui-datepicker',
	templateUrl: './datepicker.component.html',
	standalone: false,
})
export class DatepickerComponent implements FormValueControl<string | null> {
	@Inject(USER_FORMAT) public readonly userFormat = inject(USER_FORMAT);

	public readonly dateFormat: Signal<DateFormat> = computed(
		() => USER_DATE_FORMATS[this.userFormat().date]
	);

	placeholder = input<string | undefined>('');
	showWhenValid = input<boolean>(false);
	type = input<DatepickerType>(Datepickers.INPUT);
	minDate = input<Date | undefined>(new Date(2016, 0, 1));
	maxDate = input<Date | undefined>(
		new Date(new Date().setFullYear(new Date().getFullYear() + 10))
	); // today + 10 years
	syncedDate = model<Date | undefined>(undefined);
	excludedDates = input<Date[]>([]);
	inputId = input<string>('');
	invalid = input<boolean>(false);
	disabled = input<boolean>(false);
	useContainerWidth = input<boolean>(false);
	containerSelector = input<string>('.app');

	value = model<string | null>(null);

	valueChanged = output<string | null>();
	touch = output<void>();
	calendarWeekClicked = output<CalendarWeekOutput>();

	input = viewChild<DatepickerInputComponent | undefined>('datepickerInputComponent');

	constructor() {
		effect(() => {
			if (this.syncedDate()) {
				let date = this.syncedDate();
				if (typeof this.syncedDate() === 'string') {
					date = new Date(this.syncedDate()!);
				}

				this.currentlyDisplayedMonth.set(date!);
			}
		});

		effect(() => {
			const value = this.value();
			untracked(() => {
				if (value) {
					this.currentlyDisplayedMonth.set(new Date(value));
				}
			});
		});
	}

	currentlyDisplayedMonth = signal<Date>(new Date());

	onNavigationChange(date: Date) {
		this.currentlyDisplayedMonth.set(date);
	}

	onValueChange(value: string | null) {
		this.value.set(value);
		this.valueChanged.emit(value);
		this.touch.emit();
	}

	onDateSelectionChange(date: string) {
		this.onValueChange(date);
	}

	public openCalendar(): void {
		this.input()?.openCalendar();
	}

	public closeCalendar(): void {
		this.input()?.closeCalendar();
	}

	public onCalendarOpened(): void {
		this.currentlyDisplayedMonth.set(
			convertToUTCDate(this.value() || this.syncedDate() || new Date())
		);
	}

	protected readonly Datepickers = Datepickers;
}
